import { useTranslation } from 'react-i18next';
import { PlaneTakeoff } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  formatFlightClock24,
  formatFlightWeekdayDay,
  formatOperatorClock24,
} from '@/lib/disruptions/airport-tz';
import { localDay } from './board-model';

// WHEN a disruption's flight is — its scheduled departure, on every surface that names
// the flight by code: the disruptions list rows, its «Lo que sigue» card and «Resumen»'s
// list (PO 2026-11-01: «es importante mostrar el vuelo, el día y la hora, si no no sabemos
// si es de hoy o de cuándo es»).
//
// Read in the operator's clock — the «Hora local» of /flights and «Resumen»'s live clock —
// with the day in words when it is near: «Hoy 14:35», «Ayer 22:10», «Mañana 06:05», else
// «mié 28 oct 09:15». When the departure airport's clock reads otherwise, its time
// follows («· 13:35 SCL», the /flights «Hora origen»); the tooltip spells the airport-local
// day and time (Issue #662: a flight's time is its departure airport's).

export function FlightWhen({
  departureAt,
  origin,
  now,
  className,
}: {
  /** The scheduled departure (ISO UTC). */
  departureAt: string;
  /** The departure airport (IATA) — its clock, when it differs from the operator's. */
  origin?: string | undefined;
  /** The clock the day words are read at (the board's minute tick); now when absent. */
  now?: number;
  className?: string;
}) {
  const { t, i18n } = useTranslation('disruptions');
  const at = Date.parse(departureAt);
  if (!Number.isFinite(at)) return null;

  const time = formatOperatorClock24(departureAt);
  const days = localDay(at) - localDay(now ?? Date.now());
  const label =
    days === 0
      ? t('board.when.today', { time })
      : days === -1
        ? t('board.when.yesterday', { time })
        : days === 1
          ? t('board.when.tomorrow', { time })
          : t('board.when.day', {
              day: new Intl.DateTimeFormat(i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              }).format(at),
              time,
            });
  const originTime = origin ? formatFlightClock24(departureAt, origin) : '';
  const otherClock = originTime && originTime !== time ? originTime : '';
  const when = `${formatFlightWeekdayDay(departureAt, origin)} ${originTime || time}`;
  const title = origin
    ? t('board.when.title', { when, airport: origin })
    : t('board.when.titleNoAirport', { when });

  return (
    <span
      className={cn('inline-flex items-baseline gap-1 whitespace-nowrap', className)}
      title={title}
      data-testid="flight-when"
    >
      <PlaneTakeoff className="h-3.5 w-3.5 shrink-0 self-center" aria-hidden />
      <span className="sr-only">{t('board.when.departs')}</span>
      <span>{label}</span>
      {otherClock ? (
        <span className="opacity-80" data-testid="flight-when-origin">
          {t('board.when.origin', { time: otherClock, airport: origin })}
        </span>
      ) : null}
    </span>
  );
}
